import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ApiService from '../../../../services/ApiService';
import { extractApiError, formatCnpj } from './helpers';
import {
  Banner,
  Dialog,
  DialogActions,
  DialogTitle,
  FieldError,
  FieldLabel,
  MutedText,
  Overlay,
  PillButton,
  TextInput,
} from './index.style';
import { FileDrop } from './CertificateModal.style';
import { EmissorPainel } from './types';

const TAMANHO_MAX = 100 * 1024;

const certificadoSchema = z.object({
  arquivo: z
    .instanceof(File, { message: 'Escolha o arquivo do certificado (.pfx ou .p12).' })
    .refine((f) => /\.(pfx|p12)$/i.test(f.name), 'O certificado deve ser um arquivo .pfx ou .p12.')
    .refine((f) => f.size <= TAMANHO_MAX, 'O arquivo do certificado passa de 100 KB.'),
  senha: z.string().refine((s) => s.trim().length > 0, 'Informe a senha do certificado.'),
});

type CertificadoForm = z.infer<typeof certificadoSchema>;

interface CertificateModalProps {
  emissor: EmissorPainel;
  titulo: string;
  onClose: () => void;
  onSaved: (emissor: EmissorPainel) => void;
}

const CertificateModal = ({ emissor, titulo, onClose, onSaved }: CertificateModalProps) => {
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CertificadoForm>({ resolver: zodResolver(certificadoSchema) });
  const arquivo = watch('arquivo');

  const onSubmit = async (values: CertificadoForm) => {
    setErroEnvio(null);
    try {
      const { data } = await ApiService.fiscal.uploadCertificadoEmissor(emissor.id, values.arquivo, values.senha);
      onSaved(data);
    } catch (error) {
      setErroEnvio(extractApiError(error, 'Não foi possível enviar o certificado.').message);
    }
  };

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>{emissor.certificado.estado === 'AUSENTE' ? 'Carregar certificado A1' : 'Substituir certificado A1'}</DialogTitle>
        <MutedText>
          {titulo} · CNPJ {formatCnpj(emissor.empresa.cnpj)}
        </MutedText>

        <FileDrop $hasFile={!!arquivo}>
          <input
            type='file'
            accept='.pfx,.p12'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) setValue('arquivo', file, { shouldValidate: true });
            }}
          />
          {arquivo ? arquivo.name : 'Escolher arquivo .pfx ou .p12'}
        </FileDrop>
        {errors.arquivo && <FieldError>{errors.arquivo.message}</FieldError>}

        <FieldLabel>
          Senha do certificado
          <TextInput type='password' autoComplete='new-password' {...register('senha')} />
          {errors.senha && <FieldError>{errors.senha.message}</FieldError>}
        </FieldLabel>
        <MutedText>A senha é usada só neste envio e não fica guardada no Ordeno.</MutedText>

        {erroEnvio && (
          <Banner $tone='bad'>
            {erroEnvio} O certificado atual continua valendo.
          </Banner>
        )}

        <DialogActions>
          <PillButton type='button' onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </PillButton>
          <PillButton type='submit' $variant='primary' disabled={isSubmitting}>
            {isSubmitting ? 'Enviando…' : 'Enviar certificado'}
          </PillButton>
        </DialogActions>
      </Dialog>
    </Overlay>,
    document.body,
  );
};

export default CertificateModal;
